import React, { useState } from 'react';
import { navigate } from '../../App';
import { Radius, Plus, Home, Users, Heart } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

/**
 * Bottom tab bar — always visible on mobile, sticks across the bottom.
 *
 * - One unified visual style across all tabs
 * - The center "+" button is the FAB pattern: brand-filled circle, slight elevation
 * - Login-prompt modal uses the Modal primitive (no bespoke z-index black overlay)
 */
export function FooterNav({ chatId, onAddNew }) {
    const urlParams = new URLSearchParams(window.location.search);
    const currentView = urlParams.get('view') || 'home';
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    const isGuest = chatId === 'guest';

    const handleAddNew = () => {
        if (isGuest) setShowLoginPrompt(true);
        else onAddNew?.();
    };

    const items = [
        { key: 'home',      label: 'בית',     icon: Home,   view: 'home' },
        { key: 'zones',     label: 'קהילות',  icon: Users,  view: 'zones' },
        { key: 'add',       label: 'הוסף',     icon: Plus,   action: handleAddNew, isFab: true },
        { key: 'around-me', label: 'בסביבה',  icon: Radius, view: 'around-me' },
        { key: 'favorites', label: 'מועדפים', icon: Heart,  view: 'favorites' },
    ];

    return (
        <>
            <nav
                dir="rtl"
                aria-label="ניווט תחתון"
                className="fixed bottom-0 inset-x-0 z-nav print:hidden"
            >
                {/* Soft glass surface */}
                <div className="bg-surface/90 backdrop-blur-md backdrop-saturate-150 border-t border-line-subtle">
                    <div className="app-container">
                        <ul className="flex items-center justify-around px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                            {items.map((item) => {
                                const Icon = item.icon;
                                const active = !item.isFab && currentView === item.view;
                                return (
                                    <li key={item.key}>
                                        <button
                                            type="button"
                                            onClick={() => item.action ? item.action() : navigate(item.view)}
                                            aria-current={active ? 'page' : undefined}
                                            aria-label={item.label}
                                            className={clsx(
                                                'flex flex-col items-center justify-center gap-1 select-none',
                                                'transition-colors duration-200 ease-smooth',
                                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 rounded-xl',
                                                item.isFab ? 'p-0 -mt-3' : 'px-3 py-1.5',
                                            )}
                                        >
                                            {item.isFab ? (
                                                <span
                                                    className={clsx(
                                                        'inline-flex items-center justify-center w-13 h-13 rounded-pill',
                                                        'bg-brand-500 text-ink-inverse shadow-lg',
                                                        'transition-[transform,box-shadow] duration-200 ease-spring',
                                                        'hover:bg-brand-600 hover:shadow-elevated hover:scale-[1.04] active:scale-[0.97]',
                                                        'ring-4 ring-surface',
                                                    )}
                                                >
                                                    <Plus className="w-6 h-6" strokeWidth={2.5} />
                                                </span>
                                            ) : (
                                                <>
                                                    <span className={clsx(
                                                        'inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors',
                                                        active ? 'bg-brand-50 text-brand-700' : 'text-ink-muted hover:text-ink hover:bg-surface-muted',
                                                    )}>
                                                        <Icon className={clsx('w-5 h-5', active ? 'stroke-[2.4]' : 'stroke-[2]')} />
                                                    </span>
                                                    <span className={clsx(
                                                        'text-2xs leading-none',
                                                        active ? 'font-bold text-brand-700' : 'font-medium text-ink-muted',
                                                    )}>
                                                        {item.label}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </nav>

            <Modal
                isOpen={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                title="נדרשת התחברות"
                size="sm"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setShowLoginPrompt(false)}>ביטול</Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowLoginPrompt(false);
                                localStorage.removeItem('chat_id');
                                window.location.href = '/';
                            }}
                        >
                            התחבר עכשיו
                        </Button>
                    </>
                }
            >
                <p className="text-ink-muted leading-relaxed">
                    כדי ליצור תוכן חדש, צריך להתחבר עם חשבון.
                </p>
            </Modal>
        </>
    );
}

export default FooterNav;
